/**
 * Lightweight date/time utilities.
 * This wraps an underlying library to provide a consistent interface that we can modify at any time.
 */

// fecha is like a lightweight moment.js. We can always change the underlying date library if our needs grow.
import { format, parse, setGlobalDateMasks } from 'fecha'

// Add custom masks for common use cases. See existing formats in `fecha.js`.
setGlobalDateMasks({
  sqlDate: 'YYYY-MM-DD'
})

/**
 * Formats a date. format is optional, but defaults to our "standard, but compact" date representation.
 * We may change this default if we want our typical rendering to change site-wide.
 * Return a date from a number or string. By default this will parse a number as a timestamp or a SQL-formatted date.
 */
export const parseDate = (
  date: string | number | Date,
  dateFormat = 'sqlDate'
): Date | null => {
  if (!date) {
    return null
  } else if (date instanceof Date) {
    return date
  } else if (typeof date === 'number') {
    // Assume date is a timestamp, i.e., UTC.
    // Adjust by local time offset so the date portion of the local Date matches the UTC local date.
    return new Date(date + new Date().getTimezoneOffset() * 60 * 1000)
  } else {
    return parse(date, dateFormat)
  }
}

export const formatDate = (
  date: string | number | Date,
  dateFormat = 'shortDate'
): string => {
  // Try to parse the date first if it's not a date
  if (!(date instanceof Date)) {
    const parsedDate = parseDate(date)
    if (parsedDate) {
      date = parsedDate
    } else {
      // TODO: Consider throwing an error instead
      return ''
    }
  }

  return format(date, dateFormat)
}
